import axios from 'axios';
import LogRocket from 'logrocket';
import * as Sentry from '@sentry/react';
import { getRequestContext, reportToSentry } from './helpers/sentry';
import { api_versions } from './constants';
import applicationDataTransformer from './helpers/applicationDataTransformer';
import getBaseUrl from './helpers/getBaseUrl';
import addPolicyDefaults from './helpers/addPolicyDefaults';
import { pipe } from './helpers/functionalHelpers';
import { toastError, toastWarning } from './utils/toast';
import { isFeatureFlagEnabled } from './toggle_tools/featureFlagTools';

const api_types = {
  COTERIE_API: 'coterieAPI',
  PORTAL_API: 'portalAPI',
};

// only used for GET requests and coterieAPI_v1_6_with_default_tokens axios instances for anonymous rating
// Do not use for POST/PUT requests as it can affect agency attribution
const COTERIE_PARTNER_ID = 'f6f8a0bc-ae66-4e92-87a8-ffcdd6e0afa5';

const defaultAuthHeaders = { headers: { Authorization: `Token ${COTERIE_PARTNER_ID}` } };

const defaultOptions = {
  version: api_versions.V1,
};

const createAPIInstance = (api = api_types.COTERIE_API, options = defaultOptions) => {
  const { version } = options;
  const axios_instance = axios.create({
    baseURL: getBaseUrl(version)[api],
    headers: {},
  });
  const persistedTokenData = options.tokenData || localStorage.getItem('tokenData');
  if (persistedTokenData) {
    axios_instance.defaults.headers.common['Authorization'] = JSON.parse(persistedTokenData)[version];
  }
  return axios_instance;
};

const coterieAPI_v1 = createAPIInstance();
const coterieAPI_v1_4 = createAPIInstance(api_types.COTERIE_API, { version: api_versions.V1_4 });
const coterieAPI_v1_6 = createAPIInstance(api_types.COTERIE_API, { version: api_versions.V1_6 });
const coterieAPI_v1_with_default_tokens = createAPIInstance(api_types.COTERIE_API, {
  version: api_versions.V1,
});
const coterieAPI_v1_6_with_default_tokens = createAPIInstance(api_types.COTERIE_API, {
  version: api_versions.V1_6,
});
const portalAPI = createAPIInstance(api_types.PORTAL_API);

// ----- Axios Debug/Telemetry Interceptors -----
// Adds detailed request/response logging and LogRocket tracking to help
// debug issues like status=0 (network/CORS/blocked/canceled) requests.

const redactHeaders = (headers) => {
  try {
    if (!headers) return headers;
    const copy = { ...headers };
    if (copy.Authorization) copy.Authorization = 'REDACTED';
    if (copy.authorization) copy.authorization = 'REDACTED';
    return copy;
  } catch {
    return headers;
  }
};

const safeStringSize = (val) => {
  try {
    if (val == null) return 0;
    const str = typeof val === 'string' ? val : JSON.stringify(val);
    return str ? str.length : 0;
  } catch {
    return 0;
  }
};

const fullUrlFromConfig = (config = {}) => {
  const base = config.baseURL || '';
  const url = config.url || '';
  // avoid double slashes
  if (!base) return url;
  try {
    const u = new URL(url, base);
    return u.toString();
  } catch {
    return `${base}${url}`;
  }
};

const classifyAxiosError = (error) => {
  const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
  const code = error?.code;
  const isCanceled = code === 'ERR_CANCELED' || error?.message?.toLowerCase()?.includes('canceled');
  const isTimeout = code === 'ECONNABORTED' || error?.message?.toLowerCase()?.includes('timeout');
  const hasResponse = !!error?.response;
  const status = error?.response?.status ?? 0;
  const isNetwork = code === 'ERR_NETWORK' || (!hasResponse && !!error?.request);
  const isLikelyCORSOrBlocked = isNetwork && online && status === 0;
  const url = error?.config?.url || '';
  const type = hasResponse
    ? 'http_error'
    : isCanceled
    ? 'canceled'
    : isTimeout
    ? 'timeout'
    : isNetwork
    ? 'network_error'
    : 'unknown_error';

  return {
    type,
    status,
    code,
    online,
    vis: document?.visibilityState,
    swControlled: !!navigator.serviceWorker?.controller,
    proto: performance.getEntriesByName(url)[0]?.nextHopProtocol,
    isLikelyCORSOrBlocked,
    message: error?.message,
  };
};

const safeLRTrack = (event, attrs) => {
  try {
    if (typeof LogRocket?.track === 'function') {
      LogRocket.track(event, attrs);
    } else if (typeof LogRocket?.log === 'function') {
      LogRocket.log(`${event} ${JSON.stringify(attrs)}`);
    }
  } catch {
    // ignore
  }
};

const attachAxiosDebugInterceptors = (instance, label = 'axios') => {
  if (!instance?.interceptors?.request || !instance?.interceptors?.response) return;

  instance.interceptors.request.use(
    (config) => {
      // annotate start time and label for duration + attribution
      config.debug = { startTime: Date.now(), label };
      const info = {
        label,
        method: (config.method || 'get').toUpperCase(),
        url: config.url,
        baseURL: config.baseURL,
        fullUrl: fullUrlFromConfig(config),
        headers: redactHeaders(config.headers),
        payloadSize: safeStringSize(config.data),
      };
      // console-level debug for local triage
      try {
        // eslint-disable-next-line no-console
        console.debug('[axios][request]', info);
      } catch {}
      safeLRTrack('api_request', info);
      return config;
    },
    (error) => {
      // request setup error
      const info = { label, stage: 'request_setup_error', message: error?.message, code: error?.code };
      try {
        // eslint-disable-next-line no-console
        console.error('[axios][request][error]', info, error);
      } catch {}
      safeLRTrack('api_request_error', info);
      return Promise.reject(error);
    }
  );

  instance.interceptors.response.use(
    (response) => {
      const start = response?.config?.debug?.startTime;
      const durationMs = start ? Date.now() - start : undefined;
      const info = {
        label,
        method: (response?.config?.method || 'get').toUpperCase(),
        url: response?.config?.url,
        baseURL: response?.config?.baseURL,
        fullUrl: fullUrlFromConfig(response?.config),
        status: response?.status,
        durationMs,
        responseSize: safeStringSize(response?.data),
      };
      try {
        // eslint-disable-next-line no-console
        console.debug('[axios][response]', info);
      } catch {}
      safeLRTrack('api_response', info);
      return response;
    },
    (error) => {
      const start = error?.config?.debug?.startTime;
      const durationMs = start ? Date.now() - start : undefined;
      const classification = classifyAxiosError(error);
      const info = {
        label,
        method: (error?.config?.method || 'get').toUpperCase(),
        url: error?.config?.url,
        baseURL: error?.config?.baseURL,
        fullUrl: fullUrlFromConfig(error?.config),
        durationMs,
        headers: redactHeaders(error?.config?.headers),
        payloadSize: safeStringSize(error?.config?.data),
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        ...classification,
      };
      try {
        // eslint-disable-next-line no-console
        console.error('[axios][response][error]', info, error);
      } catch {}
      // Track immediately
      safeLRTrack('api_error', info);
      // Also attach the LogRocket session URL for quick navigation
      try {
        if (typeof LogRocket?.getSessionURL === 'function') {
          LogRocket.getSessionURL((sessionURL) => {
            safeLRTrack('api_error_session', { ...info, sessionURL });
          });
        }
      } catch {}
      return Promise.reject(error);
    }
  );
};

// ----- Axios Retry Interceptors -----
// Retries transient failures. Single retry for all methods.
const RETRY_MAX_ATTEMPTS = 1; // number of retries (not counting the initial attempt)
const RETRY_BASE_DELAY_MS = 300;

const computeBackoffDelay = (attempt) => {
  const base = RETRY_BASE_DELAY_MS * Math.pow(2, Math.max(0, attempt - 1));
  const jitter = Math.floor(Math.random() * 100);
  return Math.min(1500, base + jitter);
};

const shouldRetryError = (error) => {
  const classification = classifyAxiosError(error);
  const method = (error?.config?.method || 'get').toUpperCase();
  const isCanceled = classification.type === 'canceled';

  // Allow retries for GET/HEAD/OPTIONS on network, timeout, or status 0
  const isReadLike = method === 'GET' || method === 'HEAD' || method === 'OPTIONS';
  const readLikeRetriable =
    classification.type === 'network_error' || classification.type === 'timeout' || classification.status === 0;

  // Allow retries for POST/PUT only for network/no-response (status 0). Avoid timeouts to reduce side effects.
  const isWriteLike = method === 'POST' || method === 'PUT';
  const writeLikeRetriable = classification.type === 'network_error' || classification.status === 0;

  const allowed = (isReadLike && readLikeRetriable) || (isWriteLike && writeLikeRetriable);
  return allowed && !isCanceled;
};

const attachAxiosRetryStatusZeroInterceptors = (instance, label = 'axios') => {
  if (!instance?.interceptors?.response) return;

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      try {
        const config = error?.config || {};
        if (!config) return Promise.reject(error);

        // Check feature flag to determine if retry is enabled
        const isRetryEnabled = isFeatureFlagEnabled('useApiCallRetry', true);

        if (!isRetryEnabled || !shouldRetryError(error)) {
          // Show a toast for connectivity issues we won't retry (e.g., POST), once per request
          const classification = classifyAxiosError(error);
          if (
            classification.status === 0 ||
            classification.type === 'network_error' ||
            classification.type === 'timeout'
          ) {
            if (!config.toastShown) {
              config.toastShown = true;
              const message = isRetryEnabled
                ? 'Please check your network and try again.'
                : 'Please check your network and try again. (Retry disabled)';
              toastError('Connection issue detected.', message);
            }
          }
          return Promise.reject(error);
        }

        config.retryCount = config.retryCount || 0;
        if (config.retryCount < RETRY_MAX_ATTEMPTS) {
          config.retryCount += 1;

          // Track retry event in Sentry
          const classification = classifyAxiosError(error);
          const retryInfo = {
            attempt: config.retryCount,
            maxAttempts: RETRY_MAX_ATTEMPTS,
            method: (config?.method || 'get').toUpperCase(),
            url: config?.url,
            baseURL: config?.baseURL,
            fullUrl: fullUrlFromConfig(config),
            status: error?.response?.status,
            errorType: classification.type,
            label: label,
          };

          try {
            Sentry.addBreadcrumb({
              message: `API retry attempt ${config.retryCount}/${RETRY_MAX_ATTEMPTS}`,
              category: 'api.retry',
              level: 'info',
              data: retryInfo,
            });

            // Also capture as a message for better visibility in Sentry
            Sentry.captureMessage(`API Retry: ${retryInfo.method} ${retryInfo.fullUrl}`, {
              level: 'info',
              tags: {
                component: 'api_retry',
                attempt: config.retryCount.toString(),
                error_type: classification.type,
              },
              extra: retryInfo,
            });
          } catch (sentryError) {
            // Don't let Sentry errors break the retry logic
            console.warn('Failed to track retry in Sentry:', sentryError);
          }

          // Track retry event in Pendo
          try {
            if (window.pendo && typeof window.pendo.track === 'function') {
              window.pendo.track('api_retry_attempt', {
                attempt: config.retryCount,
                maxAttempts: RETRY_MAX_ATTEMPTS,
                method: (config?.method || 'get').toUpperCase(),
                url: config?.url,
                baseURL: config?.baseURL,
                fullUrl: fullUrlFromConfig(config),
                status: error?.response?.status,
                errorType: classification.type,
                label: label,
                isRetryEnabled: isRetryEnabled,
              });
            }
          } catch (pendoError) {
            // Don't let Pendo errors break the retry logic
            console.warn('Failed to track retry in Pendo:', pendoError);
          }

          // First retry: inform the user
          if (config.retryCount === 1 && !config.toastShown) {
            config.toastShown = true;
            toastWarning('Something went wrong', 'We encountered an issue. Retrying...');
          }

          const delay = computeBackoffDelay(config.retryCount);
          return new Promise((resolve) => setTimeout(resolve, delay)).then(() => instance.request(config));
        }

        return Promise.reject(error);
      } catch {
        return Promise.reject(error);
      }
    }
  );
};

// Attach interceptors to all instances and default axios client
// Order matters: attach retry first, then debug/telemetry so only final outcome is logged.
attachAxiosRetryStatusZeroInterceptors(coterieAPI_v1, 'coterieAPI_v1');
attachAxiosRetryStatusZeroInterceptors(coterieAPI_v1_4, 'coterieAPI_v1_4');
attachAxiosRetryStatusZeroInterceptors(coterieAPI_v1_6, 'coterieAPI_v1_6');
attachAxiosRetryStatusZeroInterceptors(coterieAPI_v1_with_default_tokens, 'coterieAPI_v1_default_tokens');
attachAxiosRetryStatusZeroInterceptors(coterieAPI_v1_6_with_default_tokens, 'coterieAPI_v1_6_default_tokens');
attachAxiosRetryStatusZeroInterceptors(portalAPI, 'portalAPI');
attachAxiosRetryStatusZeroInterceptors(axios, 'axios_default');
attachAxiosDebugInterceptors(coterieAPI_v1, 'coterieAPI_v1');
attachAxiosDebugInterceptors(coterieAPI_v1_4, 'coterieAPI_v1_4');
attachAxiosDebugInterceptors(coterieAPI_v1_6, 'coterieAPI_v1_6');
attachAxiosDebugInterceptors(coterieAPI_v1_with_default_tokens, 'coterieAPI_v1_default_tokens');
attachAxiosDebugInterceptors(coterieAPI_v1_6_with_default_tokens, 'coterieAPI_v1_6_default_tokens');
attachAxiosDebugInterceptors(portalAPI, 'portalAPI');
attachAxiosDebugInterceptors(axios, 'axios_default');

export const resetApiTokens = () => {
  localStorage.removeItem('tokenData');
};

export const setApiToken = (token) => {
  const tokenString = `Token ${token}`;
  const tokenData = {
    [api_versions.V1]: tokenString,
    [api_versions.V1_4]: tokenString,
    [api_versions.V1_6]: tokenString,
  };
  localStorage.setItem('tokenData', JSON.stringify(tokenData));
  coterieAPI_v1.defaults.headers.common['Authorization'] = tokenString;
  coterieAPI_v1_6.defaults.headers.common['Authorization'] = tokenString;
};

export const setApiTokenForVersion = (version, token) => {
  const tokenString = `Token ${token}`;
  const persistedTokenData = localStorage.getItem('tokenData');
  const tokenData = persistedTokenData ? JSON.parse(persistedTokenData) : {};
  const newTokenData = {
    ...tokenData,
    [version]: tokenString,
  };
  localStorage.setItem('tokenData', JSON.stringify(newTokenData));

  switch (version) {
    case api_versions.V1:
      coterieAPI_v1.defaults.headers.common['Authorization'] = tokenString;
      break;
    case api_versions.V1_6:
      coterieAPI_v1_6.defaults.headers.common['Authorization'] = tokenString;
      break;
    default:
      return;
  }
};

export const getAuthHeader = (version = api_versions.V1) => {
  switch (version) {
    case api_versions.V1_6:
      return coterieAPI_v1_6.defaults.headers.common['Authorization'];
    case api_versions.V1:
    default:
      return coterieAPI_v1.defaults.headers.common['Authorization'];
  }
};

export const getIndustryName = (industryId) => {
  return coterieAPI_v1_6.get(`/industries/${industryId}`, defaultAuthHeaders);
};

export const getAkHashDescription = (akHash) => {
  return coterieAPI_v1_6.get(`/industries/akhash/${akHash}`, defaultAuthHeaders);
};

// TODO:  Change these calls   to be like the RC1 call below, let the reducer handle the promise
export const failFastCheck = (data) => {
  const { applicationId } = data;
  delete data.applicationId;
  return coterieAPI_v1_6.post('/applications', { ...data, externalId: applicationId });
};

export const fetchExistingApplication = (applicationId) =>
  coterieAPI_v1_6.get(`/applications/${applicationId}`, defaultAuthHeaders);

export const rateCallOne = (data) => {
  delete data.endorsementIds;
  delete data.industryName;
  return coterieAPI_v1_6.post('/quotes', pipe(applicationDataTransformer, addPolicyDefaults)(data));
};

export const rateCallTwo = (data) =>
  coterieAPI_v1_6.post('/quotes/bindable', pipe(applicationDataTransformer, addPolicyDefaults)(data));

export const anonymousRateCall = (data) =>
  coterieAPI_v1_6_with_default_tokens.post(
    '/quotes/bindable',
    pipe(applicationDataTransformer, addPolicyDefaults)(data),
    defaultAuthHeaders
  );

export const bindQuote = (data) => {
  return coterieAPI_v1_6.post(`/quotes/${data.quoteId}/bind`, {
    TokenizedPaymentId: data.tokenId,
    PaymentInterval: data.paymentInterval || 'None',
    producerExternalId: data.producerExternalId,
    agencyExternalId: data.agencyExternalId,
  });
};

export const updateApplicationOnServer = (data) =>
  coterieAPI_v1_6.post('/applications', pipe(applicationDataTransformer, addPolicyDefaults)(data));

export const fetchAcknowledgements = (industryId) =>
  coterieAPI_v1_6.get(`acknowledgements/${industryId}`, defaultAuthHeaders);

export const fetchProducerInfoFromEmail = (email) =>
  coterieAPI_v1_6.get(`/validation/producer?email=${email}`, defaultAuthHeaders);

// TODO: check other gets
export const typeAhead = (address) => {
  return coterieAPI_v1_6
    .get(`address/typeahead/${address}`, defaultAuthHeaders)
    .then((res) => res.data)
    .catch((err) => Promise.reject(err));
};

export const verifyAddressStateAndZip = (state, zipcode) => {
  return coterieAPI_v1.get(`address/verifyStateZip/${state}${zipcode}`, defaultAuthHeaders);
};

export const verifyAddress = (address) => {
  const secondaryAddress = address.secondary ? ` ${address.secondary}` : '';
  const addressParams = `street=${address.street}${secondaryAddress}&city=${address.city}&state=${address.state}&zip=${address.zipCode}`;
  return coterieAPI_v1_4
    .get(`quoteflow/address/verify?${addressParams}`, defaultAuthHeaders)
    .then((res) => res.data.data)
    .catch((err) => Promise.reject(err));
};

export const fetchTypeAheadEntries = (address) => {
  const requestURL = `address/entries`;
  const queryParams = `street=${encodeURIComponent(address.street)}&secondary=${encodeURIComponent(
    address.secondary
  )}&entries=${encodeURIComponent(address.entries)}&city=${encodeURIComponent(address.city)}&state=${encodeURIComponent(
    address.state
  )}&zipCode=${encodeURIComponent(address.zipCode)}`;

  return coterieAPI_v1_6
    .get(`${requestURL}?${queryParams}`, defaultAuthHeaders)
    .then((res) => res.data)
    .catch((err) => Promise.reject(err));
};

export const emailApplicationLink = ({ email, applicationUrl, contactFirstName, quote }) => {
  const requestData = {
    email,
    applicationUrl,
    ...(contactFirstName ? { contactFirstName } : {}),
    ...(quote ? { quote } : {}),
  };

  return axios
    .post('https://coteriefunctions.azurewebsites.net/api/EmailSavedApplication', requestData)
    .then((res) => res.data)
    .catch((err) => {
      reportToSentry(err, getRequestContext({ requestData }));
      return Promise.reject(err);
    });
};

export const fetchAvailableEndorsements = (application) =>
  coterieAPI_v1_6.post('/applications/availableEndorsements', application);

export const validateEmail = (email) => coterieAPI_v1_6.get(`validation/${email}/validate`, defaultAuthHeaders);

export const mdaLead = (id) =>
  coterieAPI_v1_with_default_tokens.post(`quoteFlow/mda/lead`, { quoteExternalId: id }, defaultAuthHeaders);

export const getProhibitedOperations = (akHash) => {
  return coterieAPI_v1_6
    .get(`/appetite/akhash/${akHash}`, defaultAuthHeaders)
    .then((res) => res.data)
    .catch((err) => Promise.reject(err));
};

export const getApplicationOptions = ({ akHash, street, city, state, zip, quoteExternalId }) => {
  return coterieAPI_v1_6
    .post('/applications/options', {
      akHash,
      street,
      city,
      state,
      zip,
      ...(quoteExternalId && { quoteExternalId }),
    })
    .then((res) => res.data)
    .catch((err) => Promise.reject(err));
};
